import axios from "axios";
import { Form, Formik } from "formik";
import { graphql, useStaticQuery } from "gatsby";
import { useState } from "react";

import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Link,
  Paragraph,
  Text,
  TextField,
} from "@ms/gatsby-theme";

import type { FormikErrors, FormikValues } from "formik";
import type { FC } from "react";

const PRE_FOOTER_EMAIL_FORM_QUERY = graphql`
  query PreFooterEmailForm {
    contentJson(jsonId: { eq: "pre-footer-email-form" }) {
      preFooterSection {
        title
        caption
        formSuccess {
          title
          caption
        }
        submitButton
        bottomText
        bottomTextLink {
          url
          label
        }
      }
    }
  }
`;

const subscribeNewsletter = async (email: string) => {
  try {
    await axios.post("/users/subscribe-newsletter", { email });
  } catch (error) {
    console.error(error);
    throw Error("Unable to subscribe. Please try again later.");
  }
};
interface PreFooterEmailFormProps {}

export const PreFooterEmailForm: FC<PreFooterEmailFormProps> = () => {
  const {
    contentJson: { preFooterSection },
  } = useStaticQuery(PRE_FOOTER_EMAIL_FORM_QUERY);

  const [hasSubmitted, setHasSubmitted] = useState(false);

  const validate = (values: FormikValues) => {
    const errors: FormikErrors<FormikValues> = {};

    if (!values.email) {
      errors.email = "Required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
      errors.email = "Please enter your email in name@domain.com format";
    }

    return errors;
  };

  return (
    <Box sx={{ width: "full", py: "s1100", bg: "grey400" }}>
      <Container>
        <Flex sx={{ flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          <Heading
            variant="headingTitle02"
            sx={{
              color: "white",
              my: "s300",
              maxWidth: [325, "none"],
            }}
          >
            {preFooterSection.title}
          </Heading>
          <Paragraph variant="sansBody02Regular" sx={{ color: "white", my: "s300" }}>
            {preFooterSection.caption}
          </Paragraph>
          <Formik
            initialValues={{ email: "" }}
            validate={validate}
            validateOnChange={hasSubmitted}
            validateOnBlur={hasSubmitted}
            onSubmit={async (values, { setStatus }) => {
              setHasSubmitted(true);
              try {
                await subscribeNewsletter(values.email);
                setStatus({ type: "success" });
              } catch (error) {
                setStatus({ type: "error", message: error.message, token: error.token });
              }
            }}
          >
            {({ values, status, errors, handleChange, handleBlur }) => {
              if (status && status.type === "success") {
                return (
                  <Box>
                    <Heading as="h4" variant="sansBody03Regular" color="white">
                      {preFooterSection.formSuccess.title}
                    </Heading>
                    <Paragraph color="white">{preFooterSection.formSuccess.caption}</Paragraph>
                  </Box>
                );
              } else {
                return (
                  <Form sx={{ width: "full" }}>
                    <Box sx={{ position: "relative", maxWidth: 500, mx: "auto", my: "s500" }}>
                      <TextField
                        label="Email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter Email"
                        sx={
                          {
                            "[data-role='label']": { textAlign: "left" },
                            input: { bg: "white", borderRadius: "xs" },
                          } as const
                        }
                      />
                      {status && status.type === "error" && (
                        <Text
                          sx={{
                            position: "absolute",
                            top: "100%",
                            color: "critical500",
                            textAlign: "center",
                          }}
                        >
                          {status.message}
                        </Text>
                      )}
                      {errors.email && (
                        <Text
                          sx={{
                            position: "absolute",
                            top: "100%",
                            color: "critical500",
                            textAlign: "center",
                          }}
                        >
                          {errors.email}
                        </Text>
                      )}
                    </Box>
                    <Button
                      variant="primaryDark"
                      aria-label="Submit email address"
                      type="submit"
                      onClick={() => setHasSubmitted(true)}
                      sx={{
                        display: "block",
                        mx: "auto",
                        my: "s500",
                      }}
                    >
                      {preFooterSection.submitButton}
                    </Button>
                  </Form>
                );
              }
            }}
          </Formik>
          <Text variant="sansBody01Regular" sx={{ color: "white", my: "s200" }}>
            {preFooterSection.bottomText}{" "}
            <Link variant="textLinkPrimaryDark" href={preFooterSection.bottomTextLink.url}>
              {preFooterSection.bottomTextLink.label}
            </Link>
          </Text>
        </Flex>
      </Container>
    </Box>
  );
};
